/**
 * TransactionLogViewerPage — `/franchise/transactions`.
 *
 * Cross-service transaction log persisted via the operations slice
 * (`/v1/franchise/ops/transaction-events`). Other services bulk-record events
 * here for unified audit + CSV export.
 *
 * The page lets the operator pick a franchise, filter by source service /
 * event type / date range, and download the filtered events as CSV. Deep
 * links into the source records (mail, payments, daily summary) remain
 * available.
 */
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listFranchises,
  listTransactionEvents,
  type TransactionEvent,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function eventsToCsv(events: readonly TransactionEvent[]): string {
  const header = [
    'occurredAt',
    'sourceService',
    'eventType',
    'referenceId',
    'amount',
    'description',
  ];
  const rows = events.map((e) =>
    [
      e.occurredAt,
      e.sourceService,
      e.eventType,
      e.referenceId,
      e.amount,
      `"${e.description.replace(/"/g, '""')}"`,
    ].join(','),
  );
  return [header.join(','), ...rows].join('\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function TransactionLogViewerPage(): JSX.Element {
  const [franchiseId, setFranchiseId] = useState('');
  const [sourceService, setSourceService] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const franchises = useQuery({
    queryKey: ['franchise', 'tx-log', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const events = useQuery({
    queryKey: ['franchise', 'tx-log', 'events', franchiseId, sourceService, fromDate, toDate],
    queryFn: () =>
      listTransactionEvents({
        franchiseId,
        ...(sourceService.length > 0 ? { sourceService } : {}),
        ...(fromDate.length > 0 ? { fromDate } : {}),
        ...(toDate.length > 0 ? { toDate } : {}),
        pageSize: 200,
      }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const denied =
    events.isError && events.error instanceof ApiError &&
    (events.error.status === 401 || events.error.status === 403);

  const selected = franchises.data?.items.find((f) => f.id === franchiseId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transaction log"
        description="Cross-service audit log per franchise — filter, view, export to CSV."
      />

      <div className="bg-white border border-border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
          {franchises.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Source service</label>
          <input
            type="text"
            placeholder="mail / payment / government …"
            value={sourceService}
            onChange={(e) => setSourceService(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
      </div>

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to view its transaction events.
        </p>
      ) : events.isPending ? (
        <p className="text-sm text-muted">Loading events…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseTransactionEvent/read</code> required.
        </DeferredNotice>
      ) : events.isError ? (
        <DeferredNotice title="Service unreachable">
          {(events.error as Error).message}
        </DeferredNotice>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard title="Events (page)" value={String(events.data?.items.length ?? 0)} />
            <KPICard
              title="Total in range"
              value={String(events.data?.total ?? 0)}
            />
            <KPICard
              title="Total amount"
              value={formatNgn(events.data?.totalAmount ?? '0')}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{selected?.legalName}</p>
              <p className="font-mono text-xs text-muted">{selected?.franchiseNumber}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (events.data?.items && events.data.items.length > 0) {
                  downloadCsv(
                    `franchise-${selected?.franchiseNumber ?? franchiseId}-events.csv`,
                    eventsToCsv(events.data.items),
                  );
                }
              }}
              disabled={(events.data?.items.length ?? 0) === 0}
              data-testid="export-csv-btn"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>

          {(events.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
              No transaction events recorded for this franchise + filter.
            </p>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Occurred</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">Description</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-left px-4 py-2">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {events.data?.items.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-muted">
                        {new Date(e.occurredAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700">{e.sourceService}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{e.eventType}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{e.description}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(e.amount)}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-muted">
                        {e.referenceId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-white border border-border rounded-xl p-4">
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">
              Drill-down
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Link
                to={`/mail/items?franchiseId=${encodeURIComponent(franchiseId)}`}
                className="block border border-border rounded-lg p-3 hover:bg-gray-50"
              >
                <p className="text-sm font-semibold text-gray-900">Mail items</p>
                <p className="text-xs text-muted">
                  Open the mail-items list filtered to this franchise.
                </p>
              </Link>
              <Link
                to={`/mail/transactions/daily?franchiseId=${encodeURIComponent(franchiseId)}`}
                className="block border border-border rounded-lg p-3 hover:bg-gray-50"
              >
                <p className="text-sm font-semibold text-gray-900">Daily transaction summary</p>
                <p className="text-xs text-muted">Counter shift summary (mail + payment).</p>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
